'use client';

import React, { useLayoutEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Heading } from '../ui';
import './ThreeCardsStickyMobile.scss';

gsap.registerPlugin(ScrollTrigger);

/**
 * ThreeCardsStickyMobile - Three belief cards with mobile sticky scroll animation
 * 
 * Features:
 * - Mobile: Cards pin and stack as user scrolls (similar to Services component)
 * - Desktop: Static grid layout
 * - Animated SVG decorations on each card title
 */
export default function ThreeCardsStickyMobile() {
  const beliefsSectionRef = useRef<HTMLDivElement>(null);
  const beliefsContainerRef = useRef<HTMLDivElement>(null);
  const [hoveredDesktopCard, setHoveredDesktopCard] = useState<number | null>(null);
  
  // Refs for animated SVG elements in card titles
  const ideaEaRef = useRef<HTMLSpanElement>(null);
  const ideaEaPathRef = useRef<SVGPathElement>(null);
  const strengthRef = useRef<HTMLSpanElement>(null);
  const strengthStarPathRef = useRef<SVGPathElement>(null);
  const humanRef = useRef<HTMLSpanElement>(null);
  const humanUnderlineRef = useRef<SVGPathElement>(null);

  // Animate hand-drawn ring around "ea" in idea
  useLayoutEffect(() => {
    if (!ideaEaPathRef.current) return;

    const path = ideaEaPathRef.current;
    const pathLength = path.getTotalLength();

    gsap.set(path, {
      strokeDasharray: pathLength,
      strokeDashoffset: pathLength,
      fill: 'none',
      stroke: '#E60000',
      strokeWidth: 2.5,
      strokeLinecap: 'round',
    });

    const context = gsap.context(() => {
      gsap.to(path, {
        strokeDashoffset: 0,
        duration: 1.2,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: ideaEaRef.current,
          start: 'top 85%',
          toggleActions: 'play none none none',
        },
      });
    });

    return () => context.revert();
  }, []);

  // Animate star after "strength" - hand-drawn stroke effect
  useLayoutEffect(() => {
    if (!strengthStarPathRef.current) return;

    const path = strengthStarPathRef.current;
    const pathLength = path.getTotalLength();

    gsap.set(path, {
      strokeDasharray: pathLength,
      strokeDashoffset: pathLength,
      fill: 'none',
      stroke: '#E60000',
      strokeWidth: 1.5,
    });

    const context = gsap.context(() => {
      gsap.to(path, {
        strokeDashoffset: 0,
        duration: 1.5,
        ease: 'power1.inOut',
        scrollTrigger: {
          trigger: strengthRef.current,
          start: 'top 80%',
          toggleActions: 'play none none none',
        },
      });
    });

    return () => context.revert();
  }, []);

  // Animate underline when "human" text scrolls into view
  useLayoutEffect(() => {
    if (!humanUnderlineRef.current) return;

    const path = humanUnderlineRef.current;
    const pathLength = path.getTotalLength();
    
    gsap.set(path, {
      strokeDasharray: pathLength,
      strokeDashoffset: pathLength,
    });

    const context = gsap.context(() => {
      gsap.to(path, {
        strokeDashoffset: 0,
        duration: 1.2,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: humanRef.current,
          start: 'top 80%',
          toggleActions: 'play none none none',
        },
      });
    });

    return () => context.revert();
  }, []);

  // Mobile: Pin/stack the three belief cards as user scrolls (similar to Services)
  useLayoutEffect(() => {
    if (!beliefsSectionRef.current || !beliefsContainerRef.current) return;

    // Track current media query state
    let isMobile = window.innerWidth <= 768;
    
    // Handle resize - refresh ScrollTrigger when crossing breakpoint
    const handleResize = () => {
      const nowMobile = window.innerWidth <= 768;
      if (isMobile !== nowMobile) {
        isMobile = nowMobile;
        // Reset all card backgrounds when switching layouts
        const allCards = document.querySelectorAll('.belief-card-content');
        allCards.forEach((card, i) => {
          card.classList.remove('bg-yellow-400');
          card.classList.add('bg-[#FDFEF6]');
          // Re-apply yellow to first card on mobile
          if (i === 0 && nowMobile) {
            card.classList.add('bg-yellow-400');
            card.classList.remove('bg-[#FDFEF6]');
          }
        });
        // Kill all ScrollTriggers and refresh
        ScrollTrigger.getAll().forEach(st => {
          if (st.vars.id && st.vars.id.includes('belief-card')) {
            st.kill();
          }
        });
        // Refresh after a short delay to ensure layout has updated
        setTimeout(() => {
          ScrollTrigger.refresh(true);
        }, 100);
      }
    };
    
    window.addEventListener('resize', handleResize);

    const ctx = gsap.context(() => {
      const mm = gsap.matchMedia();

      mm.add('(max-width: 768px)', () => {
        const mobileContainer = beliefsContainerRef.current as HTMLElement | null;
        if (!mobileContainer) return;

        const cardWrappers = gsap.utils.toArray<HTMLElement>(mobileContainer.querySelectorAll('.belief-card-wrapper'));
        const cards = gsap.utils.toArray<HTMLElement>(mobileContainer.querySelectorAll('.belief-card-inner'));
        if (!cardWrappers.length || !cards.length) return;

        // Store all card elements for cross-reference
        const allCardElements = cards.map(card => 
          card.querySelector('.belief-card-content') as HTMLElement
        ).filter(Boolean);

        // Set first card to yellow on initial load
        if (allCardElements[0]) {
          allCardElements[0].classList.add('bg-yellow-400');
          allCardElements[0].classList.remove('bg-[#FDFEF6]');
        }

        // Pin positions - each card pins 64px below the previous one
        const basePinPosition = 77; // Starting position for first card
        const pinSpacing = 34; // Spacing between each card (in pixels)
        const cardMargin = 24; // mb-6 = 1.5rem = 24px (margin on first two cards)

        // Wait for layout to be ready (margins applied) before setting up ScrollTriggers
        requestAnimationFrame(() => {
          cardWrappers.forEach((wrapper, i) => {
          const card = cards[i];
          if (!card) return;
          
          const cardElement = card.querySelector('.belief-card-content') as HTMLElement;
          if (!cardElement) return;

          // Force hardware acceleration and stable positioning
          gsap.set(wrapper, { 
            transform: 'translateZ(0)',
            willChange: 'transform'
          });

          // Desired visual position from viewport top
          const desiredPosition = basePinPosition + (pinSpacing * i);
          
          // Adjust start position: cards after the first have margins above them
          // Card 0: 0px offset, Card 1: 24px offset, Card 2: 48px offset
          // We need to delay the trigger by the margin offset so cards pin at the right visual position
          const marginOffset = i * cardMargin;
          const adjustedPosition = desiredPosition + marginOffset;

          gsap.to(card, {
            transformOrigin: 'top center',
            ease: 'none',
            scrollTrigger: {
              trigger: cardElement, // Use card content as trigger for consistent positioning
              start: `top ${adjustedPosition}`, // Pin when card content top reaches adjusted position (accounts for margins)
              end: 'bottom 700',
              endTrigger: mobileContainer,
              scrub: true,
              pin: wrapper, // Still pin the wrapper
              pinSpacing: false,
              anticipatePin: 0.5, // Pre-calculate pin position to prevent jump
              invalidateOnRefresh: true,
              markers: false,
              id: `belief-card-${i + 1}`,
              onEnter: () => {
                // When this card pins, the next card should get the color
                if (i < allCardElements.length - 1) {
                  const nextCardElement = allCardElements[i + 1];
                  if (nextCardElement) {
                    nextCardElement.classList.add('bg-yellow-400');
                    nextCardElement.classList.remove('bg-[#FDFEF6]');
                  }
                  // Remove yellow from current card when it pins (next card gets it)
                  cardElement.classList.remove('bg-yellow-400');
                  cardElement.classList.add('bg-[#FDFEF6]');
                }
                // Last card keeps yellow when it pins
              },
              onLeaveBack: () => {
                // When scrolling back up and this card unpins, remove color from next card
                if (i < allCardElements.length - 1) {
                  const nextCardElement = allCardElements[i + 1];
                  if (nextCardElement) {
                    nextCardElement.classList.remove('bg-yellow-400');
                    nextCardElement.classList.add('bg-[#FDFEF6]');
                  }
                  // Restore yellow to current card when it unpins
                  cardElement.classList.add('bg-yellow-400');
                  cardElement.classList.remove('bg-[#FDFEF6]');
                } else {
                  // Last card: remove yellow when scrolling back up to reveal card 2
                  cardElement.classList.remove('bg-yellow-400');
                  cardElement.classList.add('bg-[#FDFEF6]');
                  // Give yellow back to card 2
                  if (allCardElements[i - 1]) {
                    allCardElements[i - 1].classList.add('bg-yellow-400');
                    allCardElements[i - 1].classList.remove('bg-[#FDFEF6]');
                  }
                }
              },
            },
          });
          }); // End forEach
        }); // End requestAnimationFrame
      });
    }, beliefsSectionRef);

    return () => {
      window.removeEventListener('resize', handleResize);
      try {
        ctx.revert();
      } catch (e) {
        // no-op
      }
    };
  }, []);

  const cards = [
    {
      title: (
        <>
          We believe in the power of an id
          <span ref={ideaEaRef} className="relative inline-block align-baseline">
            ea
            <svg
              className="absolute left-[calc(50%+0.9375rem)] top-[calc(50%+0.5rem)] -translate-x-1/2 -translate-y-1/2 w-[3.125rem] h-[3.125rem] md:w-[4.1875rem] md:h-[4.25rem] pointer-events-none"
              width="67"
              height="68"
              viewBox="0 0 75 75"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                ref={ideaEaPathRef}
                d="M-0.000953737 10.5341C0.0077701 9.26892 0.241197 8.06505 0.696412 6.95363C2.7723 2.10182 8.53998 -0.0303718 13.3379 0.00790768C16.7754 -0.119076 20.0912 1.27815 22.2332 3.75066C25.7149 1.13766 30.3663 1.19312 32.8463 1.46482C37.353 2.04149 41.8829 4.36679 46.6955 8.58022C50.4528 12.0277 54.3134 16.2346 54.5521 21.3388C61.2275 22.1019 66.3253 31.3601 66.2733 38.9044C66.259 40.9749 65.8341 42.8088 65.046 44.2089C63.9368 46.5421 61.5044 50.3834 57.5644 50.2341C58.5468 52.1193 59.0582 54.261 59.0432 56.4485C59.0122 60.935 56.795 64.6427 53.116 66.3711C49.4428 68.0319 44.8738 68.2238 40.5784 66.9003C37.723 65.9947 35.1131 64.1113 33.202 61.5858C32.6701 62.8137 31.4927 63.6343 30.3924 64.2788C27.5825 65.8391 24.2557 66.437 21.2566 65.9201C16.062 64.8009 8.89607 60.0439 8.93763 54.0167C8.93836 53.9102 8.93912 53.8011 8.94801 53.692C7.52811 53.8823 6.23644 53.572 5.10279 52.7666C2.69244 51.0535 1.44288 47.3894 1.0774 44.5343C1.0024 44.0037 0.965375 43.4735 0.968904 42.9617C0.985206 40.5976 1.84494 38.6446 3.45709 37.3203C4.50817 36.3325 5.89399 35.5783 7.45929 35.1474C7.80989 35.0459 8.2209 35.2306 8.33536 35.5457C8.35665 35.6056 8.36708 35.6655 8.36665 35.7278C8.36463 36.0214 8.11097 36.2249 7.86094 36.2959C7.43968 36.4177 7.00736 36.5706 6.58301 36.7469C6.01441 36.995 5.45057 37.3393 4.91059 37.7642C3.23921 39.0231 2.38649 40.745 2.37173 42.8856C2.36946 43.2156 2.38606 43.5612 2.42434 43.9096C2.61354 45.9738 3.63067 50.3039 6.13925 51.932C7.08166 52.5438 8.14388 52.6941 9.29341 52.3772C9.75297 51.4217 10.4544 51.1901 10.9229 51.2869C11.3211 51.3676 11.5875 51.6708 11.585 52.0371C11.5818 52.4969 11.1665 52.9435 10.44 53.2685C10.3891 53.5747 10.3625 53.8863 10.3604 54.1954C10.3248 59.3523 16.8752 63.6972 21.414 64.7287C23.7085 65.155 26.198 64.8293 28.6056 63.7859C29.9828 63.0991 31.703 62.2432 31.9985 60.6942C32.0163 60.4735 32.0232 60.2475 32.0249 60.0007L32.0252 59.9566C32.0377 59.728 32.1692 59.5393 32.3923 59.4369C32.6753 59.3037 33.0413 59.3271 33.2843 59.4898C33.4923 59.6185 33.6075 59.8246 33.7066 60.0071L33.7494 60.0854C34.2223 60.7329 34.7114 61.4014 35.2686 62.0184C37.6209 64.6742 41.1493 66.3171 44.9506 66.5278C47.7731 66.6823 50.2032 66.3224 52.173 65.4552C55.5683 63.9692 57.6122 60.6058 57.6408 56.4596C57.657 54.1033 57.0005 51.7683 55.7902 49.8738C55.2301 49.6828 54.6975 49.4272 54.2061 49.1068C53.8358 48.9068 53.3283 48.563 53.3026 47.9652C53.2973 47.934 53.2949 47.9028 53.2951 47.8717C53.2972 47.5573 53.5943 47.3463 53.8876 47.2964C55.222 46.9211 56.2168 48.188 56.7652 48.9114C60.269 49.7851 62.6545 46.0552 63.7689 43.7455C64.5251 42.262 64.9162 40.6149 64.9284 38.8484C64.931 38.4691 64.9146 38.0818 64.882 37.6971C64.3442 31.9102 60.7166 23.4234 54.3215 22.5245C54.0564 23.2164 53.4067 23.8172 52.6646 24.0459C52.0422 24.2365 51.4353 24.1414 50.9495 23.7795L50.9387 23.7716L50.9307 23.7612C50.6587 23.4761 50.5223 23.1868 50.5243 22.901C50.5263 22.6152 50.661 22.3564 50.923 22.114C51.4608 21.6214 52.4555 21.3243 53.1636 21.3006C52.8781 16.2897 48.9435 12.207 44.7266 8.53806C40.7563 5.29691 37.2347 3.46959 33.6482 2.78756C29.4571 2.06498 25.5 2.80672 23.0263 4.77456C23.402 5.36171 23.8546 6.1962 23.8488 7.04572C23.8465 7.37565 23.7738 7.68692 23.6335 7.97433C23.2264 8.78991 22.0938 9.40563 21.1203 8.98323L21.1122 8.98317L21.1041 8.97532C20.4773 8.60728 20.1478 8.01525 20.153 7.26705C20.1596 6.30842 20.7091 5.28339 21.2777 4.64559C18.5826 1.35348 13.9866 0.339733 8.64359 1.85912C4.07399 3.31888 1.43455 6.50666 1.4063 10.6036C1.38633 13.5002 2.67291 16.515 5.02575 19.0955C5.32725 19.4276 5.65592 19.752 5.97384 20.0633C6.39951 20.482 6.84135 20.9163 7.2314 21.3866C7.34182 21.5017 7.40593 21.645 7.40494 21.7879C7.40354 21.9905 7.27747 22.1793 7.06512 22.2922C6.82282 22.423 6.52988 22.4236 6.30022 22.2947L6.2786 22.2816L6.26246 22.2633C5.9178 21.8972 5.55688 21.5284 5.2067 21.1752C4.34478 20.2989 3.45593 19.3939 2.71147 18.3963C0.946157 15.9705 -0.0137294 13.1736 0.00454229 10.5238L-0.000953737 10.5341ZM22.0724 5.83058C21.7517 6.3168 21.5828 6.80926 21.5797 7.2613C21.5795 7.29507 21.5792 7.33144 21.5817 7.36523C21.5996 7.52644 21.6335 7.72152 21.7302 7.86248L21.7355 7.87031L21.7355 7.87811C21.7543 7.90422 21.7677 7.9199 21.7813 7.92259C21.8517 7.94126 22.0124 7.84105 22.0968 7.78967C22.326 7.60159 22.4389 7.35815 22.441 7.04381C22.4433 6.71127 22.324 6.31295 22.0724 5.83058ZM52.7044 22.6017C52.4111 22.6594 52.0849 22.7559 51.9778 22.9448C51.9778 22.9448 52.013 22.9554 52.0266 22.9581L52.0347 22.9582C52.0645 22.9584 52.0917 22.956 52.1134 22.9509C52.328 22.9057 52.535 22.7798 52.7044 22.6017Z"
                fill="#E60000"
              />
            </svg>
          </span>
          .
        </>
      ),
      description: "We're a group of curious minds, solving problems through creativity and collaboration. Rooted in what people want and need, our ideas set us apart."
    },
    {
      title: (
        <>
          We believe difference is our <span ref={strengthRef} className="relative inline-block">strength.
            <svg
              className="absolute -right-[2.1875rem] top-1/2 -translate-y-1/2 w-[1.25rem] h-[1.4375rem] md:w-[1.6875rem] md:h-[1.9375rem] pointer-events-none"
              width="27"
              height="31"
              viewBox="0 0 27 31"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <g clipPath="url(#clip0_7439_44313)">
                <path
                  ref={strengthStarPathRef}
                  d="M15.7988 0.624817C16.0364 0.617153 16.2608 0.731302 16.3916 0.948059C16.5144 1.1517 16.5307 1.40325 16.4814 1.65509V1.659C15.6538 5.65916 15.1698 9.55918 14.8691 13.3826C17.8628 11.5685 20.8675 9.71804 24.0371 8.17755L24.0742 8.159L24.1143 8.14923C24.1594 8.13856 24.2463 8.09718 24.3965 8.00568C24.5242 7.92786 24.7004 7.8122 24.8613 7.72638C25.0171 7.64329 25.2306 7.5465 25.4531 7.54572C25.5744 7.54535 25.7063 7.57325 25.8281 7.65118C25.9185 7.70908 25.9889 7.78584 26.0439 7.87189L26.0947 7.96075L26.1006 7.97443L26.1064 7.98907C26.1716 8.15593 26.1776 8.34564 26.1318 8.51447C26.0856 8.68477 25.9746 8.87282 25.7676 8.97833L25.7607 8.98126L25.7539 8.98517C22.8847 10.3043 20.1568 11.8982 17.4375 13.5408C20.1893 13.279 22.9716 13.1832 25.7432 13.7078L25.7422 13.7088C26.1268 13.7711 26.3235 14.0766 26.3652 14.3611C26.4052 14.6345 26.3194 14.9586 26.0771 15.1678L26.0537 15.1873L26.0273 15.2029C25.8082 15.3371 25.5471 15.363 25.3223 15.3582C25.0898 15.3532 24.8384 15.3133 24.6035 15.2684C24.4849 15.2457 24.365 15.2209 24.251 15.1971C24.1357 15.173 24.0259 15.1504 23.9209 15.1307C23.705 15.0901 23.5364 15.0679 23.4141 15.0711L23.3887 15.0721L23.3633 15.0692C20.8013 14.7927 18.2347 15.1183 15.625 15.4051C18.1116 18.2012 20.6648 20.9842 22.9111 24.0193L22.9365 24.0526L22.9531 24.0906C22.9723 24.1341 23.0298 24.2122 23.1475 24.3436C23.2475 24.4552 23.3928 24.608 23.5068 24.7508C23.6177 24.8896 23.7519 25.0808 23.7949 25.2986C23.8183 25.417 23.8163 25.5502 23.7646 25.6844C23.7134 25.8177 23.6237 25.9243 23.5156 26.0076L23.5039 26.0174L23.4922 26.0252C23.3411 26.1239 23.1551 26.1678 22.9795 26.1551C22.8015 26.1421 22.5954 26.0662 22.4541 25.8807L22.4463 25.8699C20.1044 22.5595 17.3779 19.5667 14.6611 16.5262C14.5287 18.9205 14.4518 21.2878 14.3955 23.6336C14.3764 25.1637 14.3372 26.6871 14.3682 28.1688L14.3848 28.5233C14.389 28.6522 14.391 28.7896 14.3867 28.9266C14.3784 29.1947 14.3464 29.4976 14.2461 29.7547V29.7557C14.18 29.9243 14.0728 30.1009 13.8965 30.2244C13.7121 30.3535 13.4921 30.3963 13.2588 30.3651L13.126 30.3475L13.0342 30.2488C12.9082 30.1136 12.8379 29.9334 12.793 29.7703C12.7458 29.5988 12.7147 29.3998 12.6943 29.1902C12.6535 28.7707 12.6503 28.2625 12.6602 27.7488C12.6805 26.6908 12.7534 25.6355 12.7158 25.0496L12.7148 25.0359V25.0213C12.7472 22.1196 12.8734 19.1668 13.0645 16.1795C12.9958 16.2203 12.9273 16.2617 12.8584 16.3026L12.8555 16.3045C10.195 17.8488 7.51042 19.2516 4.94824 20.8973C4.6785 21.0944 4.39684 21.2853 4.12207 21.4725C3.83852 21.6656 3.56071 21.857 3.29492 22.0574C2.8142 22.4199 2.38933 22.8027 2.06738 23.2557C2.10643 23.4504 2.06518 23.6413 1.98145 23.7908C1.86928 23.9911 1.64378 24.1852 1.35156 24.1854C1.09794 24.1854 0.891432 24.0374 0.764648 23.8221L0.757812 23.8113L1.08789 23.6326L0.758789 23.8104C0.538319 23.4028 0.614858 22.9652 0.813477 22.575C1.00998 22.1891 1.34941 21.7951 1.76465 21.407C2.59922 20.6271 3.83063 19.7853 5.14844 18.9764C6.47082 18.1647 7.90137 17.3732 9.14551 16.6951C9.59552 16.4499 10.0189 16.2196 10.4043 16.0096C8.52208 16.2402 6.64111 16.4822 4.76074 16.7322C4.76207 16.8917 4.72393 17.0455 4.66504 17.1668C4.52603 17.453 4.17989 17.7706 3.71582 17.6404L3.70605 17.6375L3.69727 17.6346C3.42049 17.5409 3.02595 17.4407 2.69336 17.283L2.69043 17.282C2.60725 17.2418 2.51648 17.1947 2.42676 17.1375L2.37402 17.1033L2.33398 17.0535C2.16112 16.8347 2.04519 16.5888 2.05859 16.325C2.0726 16.0511 2.22088 15.843 2.39648 15.6971C2.72251 15.4262 3.25257 15.2755 3.75293 15.1766C4.27429 15.0735 4.85194 15.0125 5.33398 14.9647C5.57835 14.9404 5.79529 14.92 5.97656 14.8992C6.1637 14.8778 6.28634 14.8587 6.35059 14.8416L6.37305 14.8358L6.39648 14.8318C8.05543 14.6073 9.72217 14.4213 11.376 14.2186L11.3828 14.2176C11.7612 14.1792 12.1399 14.1338 12.5195 14.0906C10.6351 11.8777 8.84953 9.62974 6.87598 7.53107C6.63127 7.30049 6.39023 7.05686 6.1543 6.81915C5.91229 6.57532 5.67346 6.33702 5.42773 6.11212C4.9842 5.70619 4.53231 5.35886 4.03223 5.12677C3.8501 5.20213 3.65616 5.19847 3.49414 5.14435C3.27448 5.07093 3.04436 4.88355 2.98926 4.59747V4.5965C2.94081 4.34909 3.04378 4.11906 3.22949 3.95294L3.23828 3.94513C3.59471 3.64562 4.03959 3.63778 4.45898 3.76056C4.87328 3.88192 5.32205 4.14449 5.7793 4.48224C6.69824 5.16102 7.7532 6.22143 8.79297 7.37384C9.83636 8.53027 10.8806 9.79772 11.7783 10.9012C12.3658 11.6233 12.8878 12.2687 13.3066 12.7723C13.3574 12.1301 13.4096 11.486 13.4658 10.8406L13.4668 10.8328C13.7943 7.79146 14.2509 4.70539 14.8857 1.57013L14.8867 1.56329C14.9998 1.04859 15.3476 0.639578 15.7988 0.624817Z"
                  fill="#E60000"
                  stroke="#E60000"
                  strokeWidth="0.75"
                />
              </g>
              <defs>
                <clipPath id="clip0_7439_44313">
                  <rect width="27" height="31" fill="white"/>
                </clipPath>
              </defs>
            </svg>
          </span>
        </>
      ),
      description: "More viewpoints mean fewer blind spots. We innovate with empathy, creating space for every voice to shape the work."
    },
    {
      title: (
        <>
          We believe customer experience is <span ref={humanRef} className="relative inline-block">
            human
            <svg 
              className="absolute -bottom-3 left-0 w-full h-auto max-w-[9.25rem]" 
              width="148" 
              height="12" 
              viewBox="0 0 148 12" 
              fill="none" 
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path 
                ref={humanUnderlineRef}
                d="M145.824 8.03387C146.444 8.18214 146.831 8.37579 147.014 8.6198C147.158 8.81151 147.259 9.15512 146.967 9.87051C146.516 10.5762 146.057 10.8986 145.621 11.0333C145.166 11.1738 144.664 11.1339 144.095 10.981C143.515 10.8251 142.944 10.5773 142.319 10.339C141.741 10.1187 141.111 9.90585 140.499 9.88556C114.665 4.55826 86.772 4.28921 59.4193 3.27915L59.406 3.27856C48.1033 3.14883 36.7547 3.11673 25.1902 3.85931L25.1798 3.85979L25.1688 3.86103C23.3523 4.05736 21.513 4.15782 19.6477 4.24977C17.7866 4.34151 15.8971 4.42471 14.0005 4.58675C10.2033 4.9112 6.34359 5.55448 2.49746 7.23576L2.39018 7.28265L2.31548 7.37172C1.53741 8.29486 0.934966 8.16477 0.733089 8.00396C0.512355 7.82776 0.270994 7.2184 0.922575 6.33827L1.07287 6.1496C1.72638 5.34048 2.92007 4.60846 4.60927 3.96882C6.28619 3.33386 8.39352 2.81038 10.8092 2.3816C15.6398 1.52417 21.6413 1.05543 27.763 0.803564C33.8814 0.551844 40.104 0.517314 45.3733 0.525115C50.6131 0.532873 54.9314 0.581919 57.2401 0.49936C85.9767 1.38633 115.004 1.91596 142.046 7.23366C142.541 7.49442 143.207 7.61824 143.828 7.71085C144.53 7.8156 145.217 7.8885 145.824 8.03387Z" 
                fill="#E60000" 
                stroke="#E60000"
              />
            </svg>
          </span> experience.
        </>
      ),
      description: "Our experience-first process takes a holistic approach, so our clients can deliver on their promise across every moment."
    }
  ];


  return (
    <div ref={beliefsSectionRef} className="beliefs-section max-w-8xl mx-auto mt-16 global-padding">
      <Heading
        title="A few things we believe"
        showHr={true}
        className=""
        disableAnimation={true}
      />

      <div className="mb-10 md:mb-14"></div>
      
      {/* Mobile Cards - Separate structure for ScrollTrigger */}
      <div ref={beliefsContainerRef} className="beliefs-mobile-cards block md:hidden">
        <div className="flex flex-col items-center">
          {cards.map((card, index) => (
            <div key={`mobile-${index}`} className={`belief-card-wrapper w-[18.75rem] last:mb-[10rem]`} style={{ perspective: '400px' }}>
              <div className="belief-card-inner w-full">
                <div className="belief-card-content relative h-[21.875rem] rounded-3xl bg-[#FDFEF6] border border-black py-8 px-4 flex flex-col justify-between transition-colors duration-700 ease-out select-none">
                  <h3 className="card-title-lg">
                    {card.title}
                  </h3>
                  <p className="body-sm">
                    {card.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Desktop Cards - Separate structure with hover effects */}
      <div className="hidden md:grid md:grid-cols-3 md:gap-8 md:items-start">
        {cards.map((card, index) => (
          <div
            key={`desktop-${index}`}
            className={`belief-card-wrapper-desktop w-full transition-all duration-300 ease-out ${hoveredDesktopCard === index ? (index === 0 ? 'rotate-[-5deg]' : index === 1 ? 'rotate-[2deg]' : 'rotate-[5deg]') : ''} ${hoveredDesktopCard === index ? (index === 1 ? '-translate-y-7' : '-translate-y-6') : ''}`}
            style={{ perspective: '400px' }}
            onMouseEnter={() => setHoveredDesktopCard(index)}
            onMouseLeave={() => setHoveredDesktopCard(null)}
          >
            <div className="belief-card-inner-desktop w-full">
              <div className={`belief-card-content-desktop relative rounded-3xl border border-black py-8 px-4 flex flex-col justify-between transition-all duration-300 ease-out select-none ${hoveredDesktopCard === index ? 'bg-yellow-400' : 'bg-[#FDFEF6]'}`}>
                <h3 className="card-title-lg pb-10">
                  {card.title}
                </h3>
                <p className="body-sm">
                  {card.description}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

